/**
 * CreateNoteModal — Story 5.1 (Notizen-System UI)
 *
 * Anlegen einer Notiz mit notable-Auswahl gemaess notes.html:
 *   1. Typ waehlen (Hotspot / Standortgeber / Konto)
 *   2. konkretes Objekt waehlen (Hotspot- bzw. Standortgeber-Liste;
 *      Konto = eigener Account, keine Auswahl noetig)
 *   3. Titel (optional, max. 255) + Notiztext (Pflicht, max. 10 000)
 *
 * Contract: POST /api/v2/notes mit kanonischem notable_type/notable_id/body
 * (via useCreateNote). Fuer "Konto" liefert GET /api/v2/account/me die eigene
 * User-ID — fremde IDs lehnt das BE mit 403 ab.
 *
 * Contract-Luecke (dokumentiert, nicht erfunden): die Auswahllisten laden
 * maximal per_page=100 Eintraege (API-Maximum). Bei mehr als 100 Hotspots/
 * Standortgebern zeigt das Modal einen Hinweis, dass die Liste gekuerzt ist.
 */
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Radio, Users, User as UserIcon } from 'lucide-react'
import apiClient from '../../lib/api-client'
import { ModalSheet } from '../../components/ui'
import { useHotspots } from '../hotspots/useHotspots'
import { useAccount } from '../settings/useAccount'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'
import { useCreateNote, type CreateOriginType } from './useNotes'
import type { components } from '@heliumtracker/api-types'

type Host = components['schemas']['Host']

export const NOTE_TITLE_MAX = 255
export const NOTE_BODY_MAX = 10_000

/** API-Maximum fuer per_page (siehe OpenAPI: "max 100"). */
const PICKER_PER_PAGE = 100

type PickableType = Extract<CreateOriginType, 'Hotspot' | 'Host' | 'User'>

type Props = {
  open: boolean
  onClose: () => void
}

/**
 * Standortgeber-Liste fuer den notable-Picker. Bewusst ein eigener, schlanker
 * Query statt useHosts(): dort ist per_page auf 25 fixiert, hier brauchen wir
 * das API-Maximum, damit die Auswahl moeglichst vollstaendig ist.
 */
function usePickerHosts(enabled: boolean) {
  return useQuery({
    queryKey: ['notes', 'picker', 'hosts'],
    enabled,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v2/hosts', {
        params: { query: { page: 1, per_page: PICKER_PER_PAGE } },
      })
      if (error) throw new Error('FETCH_HOSTS_FAILED')
      return {
        hosts: (data?.data ?? []) as Host[],
        count: data?.meta?.pagy?.count,
      }
    },
  })
}

export function CreateNoteModal({ open, onClose }: Props) {
  const { t } = useTranslation('notes')
  const createNote = useCreateNote()

  const [type, setType] = useState<PickableType>('Hotspot')
  const [notableId, setNotableId] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const initialFocusRef = useRef<HTMLButtonElement | null>(null)

  const hotspotsQuery = useHotspots({ per_page: PICKER_PER_PAGE }, open && type === 'Hotspot')
  const hostsQuery = usePickerHosts(open && type === 'Host')
  const { account } = useAccount()

  const typeOptions: { key: PickableType, labelKey: string, icon: typeof Radio }[] = [
    { key: 'Hotspot', labelKey: 'create.type.hotspot', icon: Radio },
    { key: 'Host', labelKey: 'create.type.host', icon: Users },
    { key: 'User', labelKey: 'create.type.account', icon: UserIcon },
  ]

  const hotspotOptions = useMemo(
    () =>
      (hotspotsQuery.data ?? []).map((h) => ({
        id: h.id,
        label: hotspotDisplayName(h.name, h.address, t('create.unknownHotspot')),
      })),
    [hotspotsQuery.data, t],
  )

  const pickerLoading =
    (type === 'Hotspot' && hotspotsQuery.isLoading) || (type === 'Host' && hostsQuery.isLoading)

  const pickerTruncated =
    (type === 'Hotspot' && (hotspotsQuery.meta?.pagy?.count ?? 0) > PICKER_PER_PAGE) ||
    (type === 'Host' && (hostsQuery.data?.count ?? 0) > PICKER_PER_PAGE)

  const resolvedNotableId =
    type === 'User' ? account?.id : notableId ? Number(notableId) : undefined

  const canSubmit =
    body.trim().length > 0 &&
    body.length <= NOTE_BODY_MAX &&
    title.length <= NOTE_TITLE_MAX &&
    resolvedNotableId != null &&
    Number.isFinite(resolvedNotableId) &&
    !createNote.isPending

  function handleTypeChange(next: PickableType) {
    setType(next)
    setNotableId('')
  }

  function resetAndClose() {
    setType('Hotspot')
    setNotableId('')
    setTitle('')
    setBody('')
    createNote.reset()
    onClose()
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit || resolvedNotableId == null) return
    createNote.mutate(
      {
        origin_type: type,
        origin_id: resolvedNotableId,
        title: title.trim() || undefined,
        text: body,
      },
      { onSuccess: () => resetAndClose() },
    )
  }

  return (
    <ModalSheet
      open={open}
      onClose={resetAndClose}
      title={t('create.heading')}
      description={t('create.description')}
      variant="dialog"
      size="md"
      initialFocusRef={initialFocusRef}
      data-testid="notes-create-modal"
    >
      <form onSubmit={handleSubmit} className="notes-create-form">
        <fieldset className="notes-create-fieldset">
          <legend className="notes-create-label">{t('create.typeLabel')}</legend>
          <div className="row notes-create-typechips" role="radiogroup" aria-label={t('create.typeLabel')}>
            {typeOptions.map((opt, idx) => {
              const Icon = opt.icon
              return (
                <button
                  key={opt.key}
                  ref={idx === 0 ? initialFocusRef : undefined}
                  type="button"
                  role="radio"
                  aria-checked={type === opt.key}
                  className={`chip ${type === opt.key ? 'is-active' : ''}`}
                  onClick={() => handleTypeChange(opt.key)}
                  data-testid={`notes-create-type-${opt.key}`}
                >
                  <Icon size={13} aria-hidden />
                  {t(opt.labelKey)}
                </button>
              )
            })}
          </div>
        </fieldset>

        {type !== 'User' && (
          <label className="notes-create-field">
            <span className="notes-create-label">
              {type === 'Hotspot' ? t('create.pickHotspot') : t('create.pickHost')}
            </span>
            <select
              className="select"
              value={notableId}
              onChange={(e) => setNotableId(e.target.value)}
              disabled={pickerLoading || createNote.isPending}
              required
              data-testid="notes-create-notable"
            >
              <option value="" disabled>
                {pickerLoading ? t('create.pickerLoading') : t('create.pickerPlaceholder')}
              </option>
              {type === 'Hotspot' &&
                hotspotOptions.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.label}
                  </option>
                ))}
              {type === 'Host' &&
                (hostsQuery.data?.hosts ?? []).map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name || t('create.unknownHost')}
                  </option>
                ))}
            </select>
            {pickerTruncated && <span className="muted notes-create-hint">{t('create.pickerTruncated')}</span>}
            {((type === 'Hotspot' && hotspotsQuery.isError) || (type === 'Host' && hostsQuery.isError)) && (
              <span className="notes-create-error" role="alert">{t('create.pickerError')}</span>
            )}
          </label>
        )}

        {type === 'User' && (
          <p className="muted notes-create-hint" data-testid="notes-create-account-hint">
            {t('create.accountHint')}
          </p>
        )}

        <label className="notes-create-field">
          <span className="notes-create-label">{t('create.titleLabel')}</span>
          <input
            type="text"
            className="input"
            maxLength={NOTE_TITLE_MAX}
            placeholder={t('addNote.placeholderTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={createNote.isPending}
            data-testid="notes-create-title"
          />
        </label>

        <label className="notes-create-field">
          <span className="notes-create-label">{t('create.bodyLabel')}</span>
          <textarea
            className="textarea"
            rows={4}
            maxLength={NOTE_BODY_MAX}
            placeholder={t('addNote.placeholderText')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={createNote.isPending}
            required
            data-testid="notes-create-body"
          />
        </label>

        <div className="row notes-create-actions">
          <button type="button" className="btn btn-ghost" onClick={resetAndClose} disabled={createNote.isPending}>
            {t('common:actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={!canSubmit} data-testid="notes-create-submit">
            {createNote.isPending ? t('addNote.submitting') : t('addNote.submit')}
          </button>
        </div>
      </form>
    </ModalSheet>
  )
}
